package com.eladfinish.bible.contest.screens.search.suggestions

import com.eladfinish.bible.contest.screens.search.history.SearchHistoryEntry
import com.eladfinish.contract.suggestions.SearchSuggestionEntry
import com.eladfinish.contract.suggestions.SearchSuggestionsSnapshot

/** One row of the autocomplete list: the expression and the count shown next to it. */
data class AutocompleteSuggestion(
    val query: String,

    /** The user's own search count for a personal row, the global installation count otherwise. */
    val count: Int,
    val origin: Origin,
) {

    enum class Origin {
        /** From this device's search history. */
        PERSONAL,

        /** From the popular searches of all users. */
        POPULAR,
    }
}

/** The two popular lists behind the chips: what is hot now, and what is popular all time. */
data class PopularLists(
    val hotNow: List<AutocompleteSuggestion>,
    val mostPopular: List<AutocompleteSuggestion>,

    /** The window `hotNow` counts over, for the heading ("this week" / "this month"). */
    val recentDays: Int,
) {

    val isEmpty: Boolean
        get() = hotNow.isEmpty() && mostPopular.isEmpty()
}

/**
 * The matching rules, kept pure so they are tested on every target. A match is a prefix of the
 * expression exactly as typed and exactly as stored: no normalization, in every search mode.
 */
object AutocompleteMatcher {

    /**
     * Personal rows first (at most [maxPersonal], so the global list stays visible), then the
     * popular ones that are not already there, all starting with [prefix], at most [max] in total.
     */
    fun forPrefix(
        prefix: String,
        personal: List<SearchHistoryEntry>,
        snapshot: SearchSuggestionsSnapshot,
        blocklist: Set<String>,
        max: Int,
        maxPersonal: Int = max / 2,
    ): List<AutocompleteSuggestion> {
        if (prefix.isEmpty() || max <= 0) return emptyList()

        val personalRows = personal
            .asSequence()
            .filter { it.key.query.startsWith(prefix) }
            .groupBy { it.key.query }
            .map { (query, entries) ->
                AutocompleteSuggestion(query = query, count = entries.sumOf { it.searchCount }, origin = AutocompleteSuggestion.Origin.PERSONAL)
            }
            .sortedByDescending { it.count }
            .take(maxPersonal)

        val taken = personalRows.mapTo(mutableSetOf()) { it.query }

        val popularRows = snapshot.entries
            .asSequence()
            .filter { it.q.startsWith(prefix) && it.q !in taken && it.q !in blocklist }
            .map { AutocompleteSuggestion(query = it.q, count = it.n, origin = AutocompleteSuggestion.Origin.POPULAR) }
            .take(max - personalRows.size)

        return personalRows + popularRows
    }

    /** The top [perGroup] by the recent count (only expressions searched recently), and the top [perGroup] all time. */
    fun popularLists(
        snapshot: SearchSuggestionsSnapshot,
        blocklist: Set<String>,
        perGroup: Int,
    ): PopularLists {
        val allowed = snapshot.entries.filter { it.q !in blocklist }

        val hotNow = allowed
            .filter { it.r > 0 }
            .sortedWith(compareByDescending<SearchSuggestionEntry> { it.r }.thenByDescending { it.n })
            .take(perGroup)
            .map { AutocompleteSuggestion(query = it.q, count = it.r, origin = AutocompleteSuggestion.Origin.POPULAR) }

        val mostPopular = allowed
            .asSequence()
            .take(perGroup)
            .map { AutocompleteSuggestion(query = it.q, count = it.n, origin = AutocompleteSuggestion.Origin.POPULAR) }
            .toList()

        return PopularLists(hotNow = hotNow, mostPopular = mostPopular, recentDays = snapshot.recentDays)
    }
}
